// React Query hooks for org-scoped lookup table CRUD (contract types, business units, etc.).

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { lookupsApi } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import type {
  LookupCategory,
  CreateLookupItemInput,
  UpdateLookupItemInput,
} from "../types";

export function useLookupItems(category: LookupCategory) {
  const { user } = useAuth();
  const orgId = user?.organisationId;

  return useQuery({
    queryKey: ["lookups", orgId, category],
    queryFn: () => lookupsApi.list(orgId!, category),
    enabled: !!orgId,
  });
}

export const useLookups = useLookupItems;

export function useCreateLookupItem() {
  const { user } = useAuth();
  const orgId = user?.organisationId;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateLookupItemInput) =>
      lookupsApi.create(orgId!, input),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["lookups", orgId, variables.category],
      });
    },
  });
}

export function useUpdateLookupItem() {
  const { user } = useAuth();
  const orgId = user?.organisationId;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      itemId,
      input,
    }: {
      itemId: string;
      input: UpdateLookupItemInput;
    }) => lookupsApi.update(orgId!, itemId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["lookups", orgId] });
    },
  });
}
